import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// useCateringProposal — drives CateringProposalSection on a single
// catering inquiry detail view. Per-inquiry instance: pass inquiryId.
//
// Mirrors useCateringFollowup exactly:
//   - cache-first read against ai_briefs (tab_key='catering',
//     sub_key=`proposal-${inquiryId}`)
//   - lazy generate via the generate-catering-proposal edge fn
//   - synchronous inFlightRef double-fire guard
//   - 5000ms UI cooldown on generate() (server-side 30s rate limit
//     still gates real spend)
//   - 24h cache window (a proposal draft stays useful all day; we'd
//     rather show the cached draft than auto-burn budget on every open)

const TAB_KEY = 'catering'
const FN_NAME = 'generate-catering-proposal'
const CACHE_WINDOW_HOURS = 24
const COOLDOWN_MS = 5_000

export function useCateringProposal(inquiryId, restaurantId) {
  // Phases: idle | generating | ready | empty | error | cached
  // - 'idle'      — no cache row within 24h; waiting on user
  // - 'generating' → edge fn in flight
  // - 'cached'    — cache hit, payload populated
  // - 'ready'     — fresh generate() succeeded, payload populated
  // - 'empty'     — payload._empty (tiny-data nudge OR timeout retryable)
  // - 'error'     — edge fn returned a structured error
  //
  // restaurantId is the INQUIRY's own restaurant_id, passed by the caller.
  // It scopes the ai_briefs cache read to the unit that actually owns the
  // inquiry — the edge fn writes the brief under that same unit. The old
  // "first active restaurant" lookup silently scoped the cache to the
  // operator's FIRST unit, so a multi-unit operator never hit cache (and,
  // worse, the edge fn used to refuse the inquiry entirely) for inquiries
  // on any non-primary unit.
  const [phase, setPhase]               = useState('idle')
  const [payload, setPayload]           = useState(null)
  const [generatedAt, setGeneratedAt]   = useState(null)
  const [error, setError]               = useState(null)
  const [cooldownRemainingMs, setCool]  = useState(0)

  // Synchronous double-fire guard.
  const inFlightRef = useRef(false)
  const cooldownTickRef = useRef(null)
  const cooldownEndsAtRef = useRef(0)

  // Reset state when the inquiry changes so opening a different
  // inquiry's modal doesn't briefly flash the previous draft.
  useEffect(() => {
    setPhase('idle')
    setPayload(null)
    setGeneratedAt(null)
    setError(null)
  }, [inquiryId])

  // Cache lookup on mount (and when inquiryId or restaurantId resolves).
  // Cache HIT within 24h → 'cached' or 'empty'. Cache MISS → 'idle'.
  useEffect(() => {
    if (!restaurantId || !inquiryId) return
    const subKey = `proposal-${inquiryId}`
    let cancelled = false
    const cutoff = new Date(Date.now() - CACHE_WINDOW_HOURS * 60 * 60 * 1000).toISOString()
    supabase
      .from('ai_briefs')
      .select('generated_at, payload')
      .eq('restaurant_id', restaurantId)
      .eq('tab_key', TAB_KEY)
      .eq('sub_key', subKey)
      .gte('generated_at', cutoff)
      .order('generated_at', { ascending: false })
      .limit(1)
      .then(({ data, error: e }) => {
        if (cancelled) return
        if (e) {
          // eslint-disable-next-line no-console
          console.warn('[useCateringProposal] cache read failed', e)
          return
        }
        const row = (data && data[0]) || null
        if (!row) return
        setPayload(row.payload || null)
        setGeneratedAt(row.generated_at)
        if (row.payload && row.payload._empty) setPhase('empty')
        else setPhase('cached')
      })
    return () => { cancelled = true }
  }, [restaurantId, inquiryId])

  // Cooldown tick.
  const startCooldown = useCallback(() => {
    cooldownEndsAtRef.current = Date.now() + COOLDOWN_MS
    setCool(COOLDOWN_MS)
    if (cooldownTickRef.current) clearInterval(cooldownTickRef.current)
    cooldownTickRef.current = setInterval(() => {
      const remaining = Math.max(0, cooldownEndsAtRef.current - Date.now())
      setCool(remaining)
      if (remaining <= 0 && cooldownTickRef.current) {
        clearInterval(cooldownTickRef.current)
        cooldownTickRef.current = null
      }
    }, 1000)
  }, [])

  useEffect(() => {
    return () => {
      if (cooldownTickRef.current) {
        clearInterval(cooldownTickRef.current)
        cooldownTickRef.current = null
      }
    }
  }, [])

  const generate = useCallback(async (_force = false) => {
    if (!restaurantId || !inquiryId) return
    if (inFlightRef.current) return
    if (cooldownRemainingMs > 0) return

    inFlightRef.current = true
    setError(null)
    setPhase('generating')
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const resp = await fetch(`${supabaseUrl}/functions/v1/${FN_NAME}`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        body: JSON.stringify({ inquiry_id: inquiryId }),
      })
      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const data = await resp.json()
      if (data?.error) {
        setPhase('error')
        setError(data.message || "We couldn't draft a proposal right now.")
        return
      }
      setPayload(data || null)
      setGeneratedAt(new Date().toISOString())
      if (data?._empty) setPhase('empty')
      else setPhase('ready')
      startCooldown()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useCateringProposal] edge fn call failed', e)
      // Deterministic calm fallback. Never the raw error per CLAUDE.md.
      setPhase('error')
      setError("We couldn't draft a proposal right now.")
    } finally {
      inFlightRef.current = false
    }
  }, [restaurantId, inquiryId, cooldownRemainingMs, startCooldown])

  return useMemo(
    () => ({
      phase,
      payload,
      generatedAt,
      cooldownRemainingMs,
      generate,
      error,
    }),
    [phase, payload, generatedAt, cooldownRemainingMs, generate, error],
  )
}
